// views/Dashboard/BonusHistory/BonusHistory.tsx
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../../../components/common/Card/Card';
import Button from '../../../components/common/Button/Button';
import Modal from '../../../components/common/Modal/Modal';
import Loading from '../../../components/common/Loading/Loading';
import { formatCurrency } from '../../../utils/helpers';
import { BonusHistoryObj, BonusType, User } from '../../../Entities';
import { useAuth } from '../../../contexts/AuthContext';
import { notify } from '../../../components/common/Toast/toastBridge';
import styles from './BonusHistory.module.css';

type Period = 'week' | 'month' | 'year' | 'all';

const BonusHistory = () => {
  const [selectedPeriod, setSelectedPeriod] = useState<Period>('month');
  const [editingBonus, setEditingBonus] = useState<any>(null);
  const [editForm, setEditForm] = useState<{ reached_profit: string | number }>({ reached_profit: '' });
  const [activeTypeFilters, setActiveTypeFilters] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [availableTypes] = useState<string[]>(BonusType.values.map((t) => t.name as string));
  const [clicked, setClicked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  const [bonuses, setBonuses] = useState<any[]>([]);
  const { user, setUser } = useAuth();
  const navigate = useNavigate();

  const getTypeInfo = (bonus_type: string) => BonusType.get({ name: bonus_type });

  const getBonusHistories = useCallback(
    async (page: number, currTotalPages: number) => {
      try {
        setLoading(true);
        const bonusHistories = await BonusHistoryObj.getAll(
          page,
          itemsPerPage,
          selectedPeriod,
          activeTypeFilters.length ? activeTypeFilters : availableTypes,
        );
        bonusHistories.sort((a, b) => (b.creation_timestamp ?? 0) - (a.creation_timestamp ?? 0));

        if (page === currTotalPages && bonusHistories.length === itemsPerPage) {
          setTotalPages((prev) => prev + 1);
        }
        setBonuses(bonusHistories);
        setLoading(false);
      } catch (error: any) {
        notify.error(error?.message || 'Nepodařilo se načíst historii');
        navigate('/dashboard');
      }
    },
    [itemsPerPage, selectedPeriod, activeTypeFilters, availableTypes, navigate],
  );

  const handleNewPage = (newPage: number) => {
    setCurrentPage(newPage);
    getBonusHistories(newPage, totalPages);
  };

  useEffect(() => {
    setCurrentPage(1);
    setTotalPages(1);
    getBonusHistories(1, 1);
  }, [getBonusHistories]);

  const toggleTypeFilter = (type: string) => {
    setCurrentPage(1);
    setTotalPages(3);
    setActiveTypeFilters((prev) => (prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]));
  };

  const handleEditBonus = (bonus: any) => {
    setEditingBonus(bonus);
    setEditForm({ reached_profit: bonus.reached_profit });
  };

  const handleSaveEdit = async () => {
    try {
      setClicked(true);
      const prevReachedProfit = editingBonus.reached_profit;
      editingBonus.reached_profit = editForm.reached_profit;
      await editingBonus.update();

      setEditingBonus(null);
      setEditForm({ reached_profit: '' });

      if (user) {
        const profitCorrection = Math.round(100 * (Number(editForm.reached_profit) - Number(prevReachedProfit))) / 100;
        user.bonus_revenue = Math.round(100 * (user.bonus_revenue + profitCorrection)) / 100;
      }
    } catch (error) {
      console.error(error);
      notify.error('Nepodařilo se bonus modifikovat');
    } finally {
      setClicked(false);
    }
  };

  const handleReturnToPending = async () => {
    try {
      setClicked(true);
      await editingBonus.delete();

      if (user) {
        user.bonus_revenue -= parseFloat(editingBonus.reached_profit);
        user.potential_bonus_revenue -= parseFloat(editingBonus.bonus.expected_value);
        user.bonus_history_len--;
        setUser(new User({ ...user }));
      }

      setBonuses(bonuses.filter((b) => b.id !== editingBonus.id));
      setEditingBonus(null);
    } catch (error) {
      console.error(error);
      notify.error('Nastala chyba s vrácením bonusu');
    } finally {
      setClicked(false);
    }
  };

  if (loading) return <Loading />;

  return (
    <div className={styles.bonusHistoryPage}>
      <h1>Historie bonusů</h1>
      <Card className={styles.tableContainer} data-tour="history-table">
        <div className={styles.tableHeader}>
          <div className={styles.filtersRow}>
            <div className={styles.typeFilters} data-tour="history-filters">
              {availableTypes.map((type) => {
                const typeInfo = getTypeInfo(type);
                const isActive = activeTypeFilters.includes(type);
                return (
                  <button
                    key={type}
                    className={`${styles.typeFilter} ${isActive ? styles.active : styles.inactive}`}
                    onClick={() => toggleTypeFilter(type)}
                    style={{
                      borderColor: isActive ? typeInfo.color : 'var(--line)',
                      backgroundColor: isActive ? `${typeInfo.color}20` : 'transparent',
                    }}
                  >
                    <span className={styles.typeIcon}>{typeInfo.icon}</span>
                    <span>{typeInfo.label}</span>
                  </button>
                );
              })}
            </div>

            <div className={styles.controlsGroup} data-tour="history-controls">
              <select
                className="form-input"
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                <option value={10}>10 na stránku</option>
                <option value={20}>20 na stránku</option>
                <option value={50}>50 na stránku</option>
                <option value={100}>100 na stránku</option>
              </select>

              <select className="form-input" value={selectedPeriod} onChange={(e) => setSelectedPeriod(e.target.value as Period)}>
                <option value="week">Tento týden</option>
                <option value="month">Tento měsíc</option>
                <option value="year">Tento rok</option>
                <option value="all">Vše</option>
              </select>
            </div>
          </div>
        </div>

        {/* Desktop table */}
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Datum</th>
                <th>Společnost</th>
                <th>Bonus</th>
                <th>Typ</th>
                <th>Reálný zisk</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {bonuses.map((bh) => {
                const typeInfo = getTypeInfo(bh.bonus.bonus_type);
                return (
                  <tr key={bh.id}>
                    <td>{new Date(bh.creation_timestamp * 1000).toLocaleDateString()}</td>
                    <td>
                      <div className={styles.companyCell}>
                        <span className={styles.companyName}>{bh.bonus.company.name}</span>
                      </div>
                    </td>
                    <td>{bh.bonus.name}</td>
                    <td>
                      <div
                        className={styles.typeBadge}
                        style={{ backgroundColor: `${typeInfo.color}20`, color: typeInfo.color, border: `1px solid ${typeInfo.color}30` }}
                      >
                        <span>{typeInfo.icon}</span>
                        <span>{typeInfo.label}</span>
                      </div>
                    </td>
                    <td className={styles.amountCell}>{bh.reached_profit ? formatCurrency(bh.reached_profit) : '✕'}</td>
                    <td>
                      <button className={styles.editButton} onClick={() => handleEditBonus(bh)} title="Upravit bonus">
                        ✏️
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Mobile cards */}
        <div className={styles.mobileCards}>
          {bonuses.map((bh) => {
            const typeInfo = getTypeInfo(bh.bonus.bonus_type);
            return (
              <div key={bh.id} className={styles.mobileCard}>
                <div className={styles.cardHeader}>
                  <div className={styles.companyInfo}>
                    <div>
                      <div className={styles.companyName}>{bh.bonus.company.name}</div>
                      <div className={styles.bonusName}>{bh.bonus.name}</div>
                    </div>
                  </div>
                  <div className={styles.cardDate}>{new Date(bh.creation_timestamp * 1000).toLocaleDateString()}</div>
                </div>
                <div className={styles.cardContent}>
                  <div
                    className={styles.typeBadge}
                    style={{ backgroundColor: `${typeInfo.color}20`, color: typeInfo.color, border: `1px solid ${typeInfo.color}30`, marginBottom: '1rem' }}
                  >
                    <span>{typeInfo.icon}</span>
                    <span>{typeInfo.label}</span>
                  </div>
                  <div className={styles.profitInfo}>
                    <div>
                      <span className={styles.label}>Predikce:</span>
                      <span className={styles.amountCell}>{formatCurrency(bh.bonus.expected_value)}</span>
                    </div>
                    <div>
                      <span className={styles.label}>Reálný zisk:</span>
                      <span className={styles.amountCell}>{bh.reached_profit ? formatCurrency(bh.reached_profit) : '✕'}</span>
                    </div>
                  </div>
                </div>
                <div className={styles.cardFooter}>
                  <button className={styles.editButton} onClick={() => handleEditBonus(bh)} title="Upravit bonus">
                    ✏️
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Pagination */}
        <div className={styles.pagination}>
          <div className={styles.paginationInfo} />
          <div className={styles.paginationControls}>
            <Button variant="secondary" onClick={() => handleNewPage(Math.max(currentPage - 1, 1))} disabled={currentPage === 1}>
              ← Předchozí
            </Button>

            <div className={styles.pageNumbers}>
              {Array.from({ length: totalPages }, (_, i) => {
                const pageNum = i + 1;
                return (
                  <button
                    key={pageNum}
                    className={`${styles.pageButton} ${currentPage === pageNum ? styles.active : ''}`}
                    onClick={() => handleNewPage(pageNum)}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>

            <Button variant="secondary" onClick={() => handleNewPage(currentPage + 1)} disabled={currentPage === totalPages}>
              Další →
            </Button>
          </div>
        </div>
      </Card>

      {editingBonus && (
        <Modal isOpen onClose={() => setEditingBonus(null)} title="Upravit bonus">
          <div className={styles.editForm}>
            <div className={styles.bonusInfo}>
              <div>
                <div className={styles.companyName}>{editingBonus.bonus.company.name}</div>
                <div className={styles.bonusName}>{editingBonus.bonus.name}</div>
              </div>
            </div>

            <div className={styles.formGroup}>
              <label>Reálný zisk (Kč):</label>
              <input
                type="number"
                className="form-input"
                value={editForm.reached_profit}
                onChange={(e) => setEditForm({ ...editForm, reached_profit: e.target.value })}
                placeholder="Zadej reálný zisk"
              />
            </div>

            <div className={styles.formActions}>
              <Button variant="secondary" onClick={() => setEditingBonus(null)}>
                Zrušit
              </Button>
              <Button variant="warning" onClick={handleReturnToPending} disabled={clicked}>
                Vrátit mezi nedokončené
              </Button>
              <Button variant="primary" onClick={handleSaveEdit} disabled={clicked}>
                Uložit
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default BonusHistory;
